'use client';

import { useSyncExternalStore } from 'react';

// Module-scope so a single interval drives every mounted live leaf; all
// callers read the same `tickNowMs` per cycle.
const TICK_INTERVAL_MS = 100;

const listeners = new Set<() => void>();
let tickNowMs = Date.now();
let intervalId: ReturnType<typeof setInterval> | null = null;

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  if (!intervalId) {
    intervalId = setInterval(() => {
      tickNowMs = Date.now();
      for (const l of listeners) l();
    }, TICK_INTERVAL_MS);
  }
  return () => {
    listeners.delete(cb);
    if (listeners.size === 0 && intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
  };
}

function getSnapshot(): number {
  return tickNowMs;
}

// Frozen pair used when a caller opts out — useSyncExternalStore needs a
// stable subscribe that never invokes its callback (no re-renders) and a
// snapshot that returns the same value on every call (referential stability
// for primitives is the value itself).
const noopSubscribe = (): (() => void) => () => {};
const frozenSnapshot = (): number => 0;

/**
 * Wall-clock time in ms, ticking every 100 ms. Single shared interval across
 * callers.
 *
 * Pass `{ enabled: false }` when the consuming component is mounted but
 * doesn't need the live tick (e.g. an `ActivityTrail` rendering a finished
 * message in history view). The hook then skips the subscription and returns
 * a frozen 0, so the component doesn't re-render 10×/sec while idle —
 * critical when the user is interacting with surrounding UI like an
 * expand/collapse animation, especially with React DevTools open.
 */
export function useTickNow({ enabled = true }: { enabled?: boolean } = {}): number {
  return useSyncExternalStore(
    enabled ? subscribe : noopSubscribe,
    enabled ? getSnapshot : frozenSnapshot,
    enabled ? getSnapshot : frozenSnapshot,
  );
}
